import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import ReactQuill from 'react-quill'
import { Button } from '@material-ui/core'
import ErrorMessage from '../../components/ErrorMessage'
import CircularSpinner from '../../components/CircularSpinner'
import EditOrganizationLayout from '../../layouts/EditOrganizationLayout'
import Navbar from '../../components/Navbar'

import 'react-quill/dist/quill.snow.css'
import { useOrganizationWLSettings } from '../../hooks/useOrganizationWLSettings'
import { OrganizationSettings } from '../../dtos/OrganizationDTO'
import HeaderWithIcon from '../../components/HeaderWithIcon'
import StatePagesIcon from 'images/white-label/state.png'

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
  },
  contentActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  mb12: {
    marginBottom: '6rem!important',
  },
  mb75: {
    marginBottom: '0.75rem',
    display: 'block',
  },
}))

const modules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
  ],
}

const EditOrganizationStatePages: React.FC = () => {
  const classes = useStyles()

  const {
    formData,
    isWhiteLabel,
    subDomain,
    isFormEdited,
    loadDataError,
    handleSubmit,
    loading,
    setFormData,
    setIsFormEdited,
    organizationId,
    loadOrganization,
  } = useOrganizationWLSettings()

  const handleChange = ({ name, value }: { name: string; value: string }) => {
    setFormData((prev) => {
      const copy = structuredClone(prev)
      const [parent, child] = name.split('.')
      if (!copy.StatePages[parent as keyof OrganizationSettings['StatePages']]) {
        copy.StatePages[parent as keyof OrganizationSettings['StatePages']] = {} as any
      }
      // @ts-ignore
      copy.StatePages[parent][child] = value
      return copy
    })
    setIsFormEdited(true)
  }

  return (
    <div>
      <div className='hero-head'>
        <Navbar />
      </div>
      {loadDataError && <ErrorMessage text={loadDataError} />}
      {loading ? (
        <CircularSpinner className='is-center' />
      ) : (
        <EditOrganizationLayout
          organizationId={organizationId}
          reloadOrganization={loadOrganization}
          isWhiteLabel={isWhiteLabel}
          subDomain={subDomain}
          isFormEdited={isFormEdited}
          isWhiteLabelPage
        >
          <div className={classes.content}>
            <div className={classes.contentActions}>
              <HeaderWithIcon
                iconUrl={StatePagesIcon}
                title='State Pages & Legal'
                description='Manage your platform content and legal documents'
              />
              <Button color='primary' variant='contained' onClick={handleSubmit}>
                Save
              </Button>
            </div>
            <div className='columns is-justify-content-center is-0'>
              <div className='column p-0'>
                <div className={`columns is-flex ${classes.mb12}`}>
                  <div className='column field'>
                    <h4 className='is-size-5 has-text-weight-semibold'>About Page</h4>
                    <div>
                      <label className={classes.mb75}>Content for your organization’s about page</label>
                      <div className='control'>
                        <ReactQuill
                          placeholder='Write your about page content here'
                          value={formData.StatePages?.AboutPage?.Content || ''}
                          onChange={(value, _delta, source) => {
                            if (source === 'user') {
                              handleChange({
                                name: 'AboutPage.Content',
                                value,
                              })
                            }
                          }}
                          style={{ height: '100%', borderRadius: '100px', margin: '-2px' }}
                          modules={modules}
                        />
                      </div>
                    </div>
                    <br />
                  </div>
                </div>
                <div className='columns is-flex mb-6'>
                  <div className='column field mt-6'>
                    <h4 className='is-size-5 has-text-weight-semibold'>Legal Documents</h4>
                    <p className='is-size-7'>Add your required legal documents</p>
                    <div className='mb-6 mt-4'>
                      <label className={classes.mb75}>Content for your Privacy Policy page</label>
                      <div className='control'>
                        <ReactQuill
                          placeholder='Write your about page content here'
                          value={formData.StatePages?.LegalDocuments?.PrivacyPolicy || ''}
                          onChange={(value, _delta, source) => {
                            if (source === 'user') {
                              handleChange({
                                name: 'LegalDocuments.PrivacyPolicy',
                                value,
                              })
                            }
                          }}
                          style={{ height: '100px', borderRadius: '100px', margin: '-2px' }}
                          modules={modules}
                        />
                      </div>
                    </div>
                    <div className='mb-6 mt-4 pt-4'>
                      <label className={classes.mb75}>Content for your Cookies Policy page</label>
                      <div className='control'>
                        <ReactQuill
                          placeholder='Write your about page content here'
                          value={formData.StatePages?.LegalDocuments?.CookiesPolicy || ''}
                          onChange={(value, _delta, source) => {
                            if (source === 'user') {
                              handleChange({
                                name: 'LegalDocuments.CookiesPolicy',
                                value,
                              })
                            }
                          }}
                          style={{ height: '100px', borderRadius: '100px', margin: '-2px' }}
                          modules={modules}
                        />
                      </div>
                    </div>
                    <div className='mb-6 mt-4 pt-4'>
                      <label className={classes.mb75}>Content for your Terms of Use page</label>
                      <div className='control'>
                        <ReactQuill
                          placeholder='Write your about page content here'
                          value={formData.StatePages?.LegalDocuments?.TermsOfUse || ''}
                          onChange={(value, _delta, source) => {
                            if (source === 'user') {
                              handleChange({
                                name: 'LegalDocuments.TermsOfUse',
                                value,
                              })
                            }
                          }}
                          style={{ height: '100px', borderRadius: '100px', margin: '-2px' }}
                          modules={modules}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </EditOrganizationLayout>
      )}
    </div>
  )
}

export default EditOrganizationStatePages
